import {
  Calendar,
  CheckCircle2,
  Copy,
  Database,
  Eye,
  EyeOff,
  Loader2,
  Plug,
  PlugZap,
  RefreshCw,
  Trash2,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import {
  type CredentialField,
  type RosterIntegrationDto,
  type RosterProvider,
  type RosterProviderDto,
  useDeleteRoster,
  useRosterIntegrations,
  useRosterProviders,
  useRosterSync,
  useUpsertRoster,
} from '../api/useRosterIntegrations.js';
import { cn } from '../lib/cn.js';

// Settings → Roster integrations.
//
// Each provider card shows the webhook URL (copyable), capability badges, and whether
// an API token / signing secret / SQS queue are configured. The editor modal lets
// admins paste secrets and sync-now (for providers that support API pull).

export function RosterIntegrationsSection({ canEdit }: { canEdit: boolean }) {
  const providers = useRosterProviders();
  const integrations = useRosterIntegrations();
  const [editing, setEditing] = useState<
    | { mode: 'new'; provider: RosterProvider; caps: RosterProviderDto }
    | { mode: 'edit'; existing: RosterIntegrationDto; caps: RosterProviderDto }
    | null
  >(null);

  const configured = new Set((integrations.data?.integrations ?? []).map((i) => i.provider));
  const catalog = providers.data?.providers ?? [];
  const available = catalog.filter((p) => !configured.has(p.provider));

  function capsFor(provider: RosterProvider): RosterProviderDto | undefined {
    return catalog.find((p) => p.provider === provider);
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow-panel">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-lg font-semibold">Roster integrations</h2>
          <p className="mt-0.5 text-xs text-ink-faint">
            Sync scheduled visits from AlayaCare, ShiftCare, and others. Events flow in via webhook
            or SQS; updates (arrived, completed, cancelled) land on the Visits page in real time.
          </p>
        </div>
      </div>

      {(providers.isError || integrations.isError) && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          Failed to load providers:{' '}
          {(providers.error as Error | undefined)?.message ?? 'unknown error'}
          {integrations.error ? ` · ${(integrations.error as Error).message}` : ''}
        </div>
      )}

      {providers.isLoading && (
        <div className="py-6 text-center text-xs text-ink-faint">Loading providers…</div>
      )}

      <div className="space-y-3">
        {(integrations.data?.integrations ?? []).map((it) => {
          const caps = capsFor(it.provider);
          return (
            <IntegrationCard
              key={it.id}
              integration={it}
              canEdit={canEdit}
              caps={caps}
              onEdit={() => caps && setEditing({ mode: 'edit', existing: it, caps })}
            />
          );
        })}
      </div>

      {canEdit && available.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-line pt-4">
          {available.map((p) => (
            <button
              key={p.provider}
              type="button"
              onClick={() => setEditing({ mode: 'new', provider: p.provider, caps: p })}
              className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-line-strong bg-surface-3 px-3 py-1.5 text-xs font-medium text-ink-dim hover:border-brand-purple hover:text-brand-purple"
            >
              <Plug className="h-3.5 w-3.5" />
              Connect {p.display_name}
            </button>
          ))}
        </div>
      )}

      {!providers.isLoading &&
        !providers.isError &&
        (integrations.data?.integrations ?? []).length === 0 &&
        available.length === 0 && (
          <div className="py-6 text-center text-xs text-ink-faint">
            No roster providers available on this install.
          </div>
        )}

      {editing && (
        <IntegrationEditor
          mode={editing.mode}
          caps={editing.caps}
          existing={editing.mode === 'edit' ? editing.existing : undefined}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// Credential readiness = every credential field the adapter declares has a stored
// value (tracked via the has_* flags on the integration DTO).
function hasAllCredentials(caps: RosterProviderDto | undefined, i: RosterIntegrationDto): boolean {
  if (!caps) return false;
  return caps.credential_fields.every((f) => {
    if (f.key === 'api_token') return i.has_api_token;
    if (f.key === 'api_public_key') return i.has_api_public_key;
    if (f.key === 'api_private_key') return i.has_api_private_key;
    return false;
  });
}

function IntegrationCard({
  integration,
  canEdit,
  caps,
  onEdit,
}: {
  integration: RosterIntegrationDto;
  canEdit: boolean;
  caps?: RosterProviderDto;
  onEdit: () => void;
}) {
  const del = useDeleteRoster();
  const sync = useRosterSync();
  const credsReady = hasAllCredentials(caps, integration);
  const canPull = Boolean(caps?.supports_pull && credsReady);

  async function onSync() {
    await sync.mutateAsync({ provider: integration.provider });
  }

  return (
    <div className="rounded-md border border-line bg-surface-3 p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PlugZap
            className={cn('h-4 w-4', integration.active ? 'text-emerald-600' : 'text-ink-mute')}
          />
          <div>
            <div className="text-sm font-semibold text-ink capitalize">
              {integration.display_name ?? integration.provider.replace('_', ' ')}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-ink-faint">
              <span>{integration.active ? 'Active' : 'Disabled'}</span>
              {caps?.supports_webhook && (
                <span className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-indigo-700 ring-1 ring-inset ring-indigo-200">
                  webhook
                </span>
              )}
              {caps?.supports_sqs && integration.has_sqs_queue_url && (
                <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-amber-700 ring-1 ring-inset ring-amber-200">
                  sqs
                </span>
              )}
              {caps?.supports_pull && (
                <span
                  className={cn(
                    'rounded-full px-1.5 py-0.5 ring-1 ring-inset',
                    credsReady
                      ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                      : 'bg-surface-3 text-ink-faint ring-gray-200',
                  )}
                >
                  {credsReady ? 'pull ready' : 'pull needs credentials'}
                </span>
              )}
              {!integration.has_webhook_signing_secret && (
                <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-amber-700 ring-1 ring-inset ring-amber-200">
                  no signing secret
                </span>
              )}
            </div>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            {canPull && (
              <button
                type="button"
                onClick={onSync}
                disabled={sync.isPending}
                className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-brand-purple hover:bg-brand-purple/10 disabled:opacity-40"
              >
                {sync.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <RefreshCw className="h-3 w-3" />
                )}
                Sync now
              </button>
            )}
            <button
              type="button"
              onClick={onEdit}
              className="rounded px-2 py-0.5 text-xs font-medium text-brand-purple hover:bg-brand-purple/10"
            >
              Configure
            </button>
            <button
              type="button"
              disabled={del.isPending}
              onClick={() => {
                if (window.confirm(`Disconnect ${integration.provider}?`))
                  del.mutate(integration.id);
              }}
              className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs text-red-700 hover:bg-red-50 disabled:opacity-40"
            >
              <Trash2 className="h-3 w-3" />
              Disconnect
            </button>
          </div>
        )}
      </div>
      {sync.isSuccess && sync.data && (
        <div
          className={cn(
            'mb-2 flex flex-col gap-1 rounded-md px-2 py-1 text-[11px] ring-1 ring-inset',
            sync.data.polled === 0
              ? 'bg-surface-3 text-ink-dim ring-gray-200'
              : sync.data.fetched === 0
                ? 'bg-amber-50 text-amber-900 ring-amber-200'
                : 'bg-emerald-50 text-emerald-800 ring-emerald-200',
          )}
        >
          <div className="flex items-start gap-1">
            <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0" />
            <div>
              <div>
                Polled {sync.data.polled} worker{sync.data.polled === 1 ? '' : 's'} · fetched{' '}
                {sync.data.fetched} · applied {sync.data.applied}
                {sync.data.skipped > 0 ? ` · skipped ${sync.data.skipped}` : ''}
              </div>
              {sync.data.polled === 0 && (
                <div className="mt-0.5 text-[10px] text-ink-faint">
                  No workers have an AlayaCare employee id linked. Roster → Edit a worker → set the
                  id under "Roster system IDs".
                </div>
              )}
              {sync.data.polled > 0 && sync.data.fetched === 0 && (
                <div className="mt-0.5 text-[10px]">
                  Zero visits returned. Expand the request trace below to see exactly what the
                  adapter asked AlayaCare and what the response said.
                </div>
              )}
            </div>
          </div>
          {sync.data.trace.length > 0 && (
            <details className="mt-1">
              <summary className="cursor-pointer text-[10px] opacity-80 hover:opacity-100">
                Request trace ({sync.data.trace.length} call
                {sync.data.trace.length === 1 ? '' : 's'})
              </summary>
              <ul className="mt-1 flex flex-col gap-1 font-mono text-[10px]">
                {sync.data.trace.map((t, i) => (
                  <li key={`${t.url}-${i}`} className="break-all">
                    <span className="font-sans opacity-70">
                      HTTP {t.status} · items={t.returned_count}
                      {t.total_pages !== null ? ` · total_pages=${t.total_pages}` : ''}
                    </span>
                    <br />
                    <code>{t.url}</code>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
      {sync.isError && (
        <div className="mb-2 inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-0.5 text-[11px] text-red-800 ring-1 ring-inset ring-red-200">
          Sync failed: {(sync.error as Error).message}
        </div>
      )}
      <CopyField label="Webhook URL" value={integration.webhook_url} />
      {integration.api_base_url && (
        <div className="mt-2 text-[11px] text-ink-faint">
          <span className="font-medium">API base:</span>{' '}
          <span className="font-mono">{integration.api_base_url}</span>
        </div>
      )}
      {integration.sqs_queue_url && (
        <div className="text-[11px] text-ink-faint">
          <span className="font-medium">SQS queue:</span>{' '}
          <span className="font-mono">{integration.sqs_queue_url}</span>
          {integration.sqs_region && (
            <span className="ml-1 text-ink-mute">({integration.sqs_region})</span>
          )}
          {(integration.has_sqs_key || integration.has_sqs_secret) && (
            <span className="ml-2 text-emerald-700">
              · key {integration.has_sqs_key ? '✓' : '—'} / secret{' '}
              {integration.has_sqs_secret ? '✓' : '—'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  async function doCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* no-op */
    }
  }
  return (
    <div>
      <div className="mb-0.5 text-[11px] font-medium uppercase tracking-wider text-ink-faint">
        {label}
      </div>
      <div className="flex items-center gap-1">
        <code className="flex-1 overflow-x-auto rounded bg-white px-2 py-1 font-mono text-[11px] ring-1 ring-inset ring-gray-200">
          {value}
        </code>
        <button
          type="button"
          onClick={doCopy}
          className="inline-flex items-center gap-1 rounded border border-line bg-white px-2 py-1 text-[11px] hover:bg-surface-3"
        >
          {copied ? (
            <CheckCircle2 className="h-3 w-3 text-emerald-600" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function IntegrationEditor({
  mode,
  caps,
  existing,
  onClose,
}: {
  mode: 'new' | 'edit';
  caps: RosterProviderDto;
  existing?: RosterIntegrationDto;
  onClose: () => void;
}) {
  const upsert = useUpsertRoster();
  const [displayName, setDisplayName] = useState(existing?.display_name ?? '');
  const [apiBaseUrl, setApiBaseUrl] = useState(existing?.api_base_url ?? '');
  // One entry per adapter-declared credential field. Keyed by field.key so we can
  // forward values into the upsert payload without hard-coding provider knowledge.
  const [credValues, setCredValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of caps.credential_fields) {
      // Non-secret fields (public_key) pre-fill from the stored value; secrets stay blank
      // and the server keeps the existing value if we don't send a new one.
      if (!f.secret && f.key === 'api_public_key') init[f.key] = existing?.api_public_key ?? '';
      else init[f.key] = '';
    }
    return init;
  });
  const [shownSecrets, setShownSecrets] = useState<Record<string, boolean>>({});
  const [webhookSecret, setWebhookSecret] = useState('');
  const [sqsQueueUrl, setSqsQueueUrl] = useState(existing?.sqs_queue_url ?? '');
  const [sqsRegion, setSqsRegion] = useState(existing?.sqs_region ?? '');
  const [sqsKey, setSqsKey] = useState(existing?.sqs_key ?? '');
  const [sqsSecret, setSqsSecret] = useState('');
  const [showSqsSecret, setShowSqsSecret] = useState(false);
  const [active, setActive] = useState(existing?.active ?? true);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  function credAlreadyStored(key: CredentialField['key']): boolean {
    if (!existing) return false;
    if (key === 'api_token') return existing.has_api_token;
    if (key === 'api_public_key') return existing.has_api_public_key;
    if (key === 'api_private_key') return existing.has_api_private_key;
    return false;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const patch: Parameters<typeof upsert.mutateAsync>[0] = {
      provider: caps.provider,
      display_name: displayName.trim() || undefined,
      active,
    };
    if (apiBaseUrl.trim() !== (existing?.api_base_url ?? '')) {
      patch.api_base_url = apiBaseUrl.trim() || null;
    }
    // Only send credential values when the user typed something. This preserves the
    // "leave blank to keep existing" semantic for secrets; non-secret public_key fields
    // send whenever they differ from the stored value.
    for (const f of caps.credential_fields) {
      const current = credValues[f.key] ?? '';
      const trimmed = current.trim();
      if (f.secret) {
        if (trimmed) patch[f.key] = trimmed;
      } else {
        const storedValue = (f.key === 'api_public_key' ? existing?.api_public_key : '') ?? '';
        if (trimmed !== storedValue) patch[f.key] = trimmed || null;
      }
    }
    if (webhookSecret.trim()) patch.webhook_signing_secret = webhookSecret.trim();
    if (sqsQueueUrl.trim() !== (existing?.sqs_queue_url ?? '')) {
      patch.sqs_queue_url = sqsQueueUrl.trim() || null;
    }
    if (sqsRegion.trim() !== (existing?.sqs_region ?? '')) {
      patch.sqs_region = sqsRegion.trim() || null;
    }
    // SQS key is non-secret (echoes back) — send whenever it differs from the stored
    // value, including when cleared. SQS secret is masked: only send if the user
    // typed something new, mirroring the private_key behaviour.
    if (sqsKey.trim() !== (existing?.sqs_key ?? '')) {
      patch.sqs_key = sqsKey.trim() || null;
    }
    if (sqsSecret.trim()) patch.sqs_secret = sqsSecret.trim();
    await upsert.mutateAsync(patch);
    onClose();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="roster-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        role="document"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-lg bg-white p-5 shadow-2xl"
      >
        <h2
          id="roster-title"
          className="mb-1 flex items-center gap-2 text-base font-semibold text-ink"
        >
          <Calendar className="h-4 w-4 text-brand-purple" />
          {mode === 'new' ? 'Connect' : 'Configure'} {caps.display_name}
        </h2>
        <p className="mb-4 text-xs text-ink-faint">
          {caps.supports_pull && 'Pull upcoming visits via API · '}
          {caps.supports_webhook && 'Receive updates via webhook · '}
          {caps.supports_sqs && 'Subscribe to SQS queue · '}
        </p>

        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          {caps.supports_pull && (
            <>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-ink-dim">API base URL</span>
                <input
                  type="url"
                  value={apiBaseUrl}
                  onChange={(e) => setApiBaseUrl(e.target.value)}
                  placeholder={
                    caps.provider === 'alayacare'
                      ? 'https://<tenant>.alayacare.com/ext/api/v2/scheduler'
                      : 'https://api.example.com/v1'
                  }
                  className="rounded-md border border-line-strong px-3 py-1.5 font-mono text-xs focus:border-brand-purple focus:outline-none"
                />
                {caps.provider === 'alayacare' && (
                  <span className="text-[10px] text-ink-faint">
                    AlayaCare's Scheduler API is at <code>/ext/api/v2/scheduler</code>. If you paste
                    just <code>/ext/api/v2</code> we auto-append the suffix on requests.
                  </span>
                )}
              </label>
              {caps.credential_fields.map((field) => {
                const shown = shownSecrets[field.key] ?? !field.secret;
                const hasStored = credAlreadyStored(field.key);
                return (
                  <label key={field.key} className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-ink-dim">
                      {field.label}
                      {field.secret && hasStored && (
                        <span className="ml-1 text-[10px] text-ink-faint">
                          (leave blank to keep existing)
                        </span>
                      )}
                    </span>
                    <div className="flex items-center gap-1">
                      <input
                        type={field.secret && !shown ? 'password' : 'text'}
                        value={credValues[field.key] ?? ''}
                        onChange={(e) =>
                          setCredValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                        }
                        placeholder={
                          field.secret && hasStored ? '••••••••' : (field.help_text ?? field.label)
                        }
                        className="flex-1 rounded-md border border-line-strong px-3 py-1.5 font-mono text-xs focus:border-brand-purple focus:outline-none"
                      />
                      {field.secret && (
                        <button
                          type="button"
                          onClick={() =>
                            setShownSecrets((prev) => ({ ...prev, [field.key]: !shown }))
                          }
                          className="rounded border border-line bg-white px-2 py-1 text-xs text-ink-faint hover:bg-surface-3"
                        >
                          {shown ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </button>
                      )}
                    </div>
                    {field.help_text && (
                      <span className="text-[10px] text-ink-faint">{field.help_text}</span>
                    )}
                  </label>
                );
              })}
            </>
          )}

          {caps.supports_webhook && (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-dim">
                Webhook signing secret{' '}
                {existing?.has_webhook_signing_secret && (
                  <span className="text-[10px] text-ink-faint">(leave blank to keep existing)</span>
                )}
              </span>
              <div className="flex items-center gap-1">
                <input
                  type={showWebhookSecret ? 'text' : 'password'}
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                  placeholder={
                    existing?.has_webhook_signing_secret
                      ? '••••••••'
                      : 'Paste the HMAC secret from the provider'
                  }
                  className="flex-1 rounded-md border border-line-strong px-3 py-1.5 font-mono text-xs focus:border-brand-purple focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowWebhookSecret((v) => !v)}
                  className="rounded border border-line bg-white px-2 py-1 text-xs text-ink-faint hover:bg-surface-3"
                >
                  {showWebhookSecret ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                </button>
              </div>
            </label>
          )}

          {caps.supports_sqs && (
            <>
              <div className="grid grid-cols-[2fr_1fr] gap-2">
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-ink-dim">SQS queue URL (optional)</span>
                  <input
                    type="url"
                    value={sqsQueueUrl}
                    onChange={(e) => setSqsQueueUrl(e.target.value)}
                    placeholder="https://sqs.ap-southeast-2.amazonaws.com/123/…"
                    className="rounded-md border border-line-strong px-3 py-1.5 font-mono text-xs focus:border-brand-purple focus:outline-none"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-ink-dim">Region</span>
                  <input
                    type="text"
                    value={sqsRegion}
                    onChange={(e) => setSqsRegion(e.target.value)}
                    placeholder="ap-southeast-2"
                    className="rounded-md border border-line-strong px-3 py-1.5 font-mono text-xs focus:border-brand-purple focus:outline-none"
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-ink-dim">SQS key</span>
                  <input
                    type="text"
                    value={sqsKey}
                    onChange={(e) => setSqsKey(e.target.value)}
                    placeholder="Provided by the roster vendor"
                    className="rounded-md border border-line-strong px-3 py-1.5 font-mono text-xs focus:border-brand-purple focus:outline-none"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-ink-dim">
                    SQS secret
                    {existing?.has_sqs_secret && !sqsSecret && (
                      <span className="ml-1 text-[10px] font-normal text-ink-faint">
                        · leave blank to keep stored secret
                      </span>
                    )}
                  </span>
                  <div className="relative">
                    <input
                      type={showSqsSecret ? 'text' : 'password'}
                      value={sqsSecret}
                      onChange={(e) => setSqsSecret(e.target.value)}
                      placeholder={
                        existing?.has_sqs_secret ? '•••••• stored' : 'Provided by the roster vendor'
                      }
                      className="w-full rounded-md border border-line-strong px-3 py-1.5 pr-8 font-mono text-xs focus:border-brand-purple focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSqsSecret((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-mute hover:text-ink-faint"
                      aria-label={showSqsSecret ? 'Hide SQS secret' : 'Show SQS secret'}
                    >
                      {showSqsSecret ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    </button>
                  </div>
                </label>
              </div>
            </>
          )}

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-dim">Display name (optional)</span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={120}
              className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            />
          </label>

          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-line-strong text-brand-purple"
            />
            Active
          </label>

          {upsert.isError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              {(upsert.error as Error).message}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-line pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-line bg-white px-3 py-1.5 text-sm font-medium text-ink-dim hover:bg-surface-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={upsert.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
            >
              {upsert.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Zap className="h-3.5 w-3.5" />
              )}
              {mode === 'new' ? 'Connect' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Icons referenced only through string class usage — keep them imported so Vite's
// tree-shake doesn't warn. Deliberate.
void Database;
